/** Pure helpers behind the Users table's in-browser search, filters and sorting. */

import type { UserAccount } from '@/api/types';
import { inCatalogOrder, roleLabel } from '@/ui/format';

export type SortKey = 'display_name' | 'email' | 'auth_source' | 'role' | 'status' | 'created_at' | 'last_login_at';
/** The Role filter's value for an account without a role. */
export const NO_ROLE = 'none';
export type UserSort = { key: SortKey; order: 'asc' | 'desc' };

/** Each column's sort value; a never-signed-in account sorts as the oldest. */
const SORT_VALUE: Record<SortKey, (u: UserAccount) => string> = {
  display_name: (u) => u.display_name.toLowerCase(),
  email: (u) => u.email,
  auth_source: (u) => u.auth_source,
  role: (u) => (u.roles.length > 0 ? inCatalogOrder(u.roles).map(roleLabel).join(', ').toLowerCase() : NO_ROLE),
  status: (u) => (u.is_active ? '0' : '1'),
  created_at: (u) => u.created_at,
  last_login_at: (u) => u.last_login_at ?? '',
};

export interface UserFilters {
  search: string;
  sources: string[];
  roles: string[];
  statuses: string[];
}

export function statusOf(u: UserAccount): 'active' | 'inactive' {
  return u.is_active ? 'active' : 'inactive';
}

function matchesText(u: UserAccount, needle: string): boolean {
  return !needle || u.display_name.toLowerCase().includes(needle) || u.email.includes(needle) || !!u.upn?.includes(needle);
}

/** An empty filter lets everything through. */
function within(chosen: string[], value: string): boolean {
  return chosen.length === 0 || chosen.includes(value);
}

/** An account passes when it holds any chosen role, or has none and "No role" is chosen. */
function withinRoles(chosen: string[], u: UserAccount): boolean {
  if (chosen.length === 0) return true;
  return u.roles.length === 0 ? chosen.includes(NO_ROLE) : u.roles.some((role) => chosen.includes(role));
}

function matches(u: UserAccount, needle: string, f: UserFilters): boolean {
  return (
    matchesText(u, needle) &&
    within(f.sources, u.auth_source) &&
    withinRoles(f.roles, u) &&
    within(f.statuses, statusOf(u))
  );
}

export function visibleUsers(rows: UserAccount[], f: UserFilters, sort: UserSort): UserAccount[] {
  const needle = f.search.trim().toLowerCase();
  const value = SORT_VALUE[sort.key];
  const direction = sort.order === 'asc' ? 1 : -1;
  return rows.filter((u) => matches(u, needle, f)).sort((a, b) => direction * value(a).localeCompare(value(b)));
}

/** The same column flips its order; a new one starts newest first for dates, A to Z otherwise. */
export function nextSort(s: UserSort, key: SortKey): UserSort {
  if (s.key === key) return { key, order: s.order === 'asc' ? 'desc' : 'asc' };
  return { key, order: key === 'created_at' || key === 'last_login_at' ? 'desc' : 'asc' };
}
